/**
 * Anti-cheat: one validator, two entry points. `checkData` and
 * `reportPlayer` (reference/cloudscript-export-2026-08-27.js, lines ~1692
 * and ~632) run substantially the same stat validation against different
 * targets — `checkData` validates the caller's own submitted save blob,
 * `reportPlayer` validates another player's stored Chamber of Trial data.
 * Porting them as two independent functions would reproduce the threshold
 * drift that already exists between them (and a third variant, the dead
 * `checkCOT`, drifted a third way). This module has ONE exported entry
 * point, `validatePlayerData`, called once per path with a `source` tag —
 * the shared attribute-point formula and the threshold constants live in
 * exactly one place either way.
 *
 * Pure logic — no fetch, no D1, no PlayFab. Importable by the Worker the
 * same way trial-schema.ts and news-schema.ts are.
 *
 * ─── Thresholds: resolved ────────────────────────────────────────
 * checkData and reportPlayer had drifted apart on the ATK ceiling — 9999 on
 * the save path, 3000 on the report path, more than 3x apart for what is
 * nominally the same limit. The port deliberately preserved both rather than
 * guessing, and the owner resolved it on 2026-08-28: **9999 everywhere.**
 *
 * The drift was the defect, not either number. 3000 was the older value, and
 * keeping it on the report path meant a player could pass the save-path check
 * and still be flagged the moment someone reported them.
 *
 * Enhancement updated to 15 by the owner on 2026-09-30 for newest gear.
 */

import { HERO_NAMES } from "@/types/cloud-data";

export const ANTICHEAT_THRESHOLDS = {
  // Matches MenuStatus.ShowStatus / PauseManager: 15 base + 3 allocated AP
  // per level. HeroSaveData and GetTouchData store allocated attributes;
  // the UI's additional (LEVEL - 1) per stat is not saved in these fields.
  ATTRIBUTE_BASE_PT: 15,
  ATTRIBUTE_TOLERANCE: 10,
  ATTRIBUTE_PT_PER_LEVEL: 3,

  // save_check path (checkData, live) — validates the caller's own
  // self-submitted "phData" blob.
  SAVE_RUBY_MAX: 99_999,
  SAVE_ATK_MAX: 9_999,

  // player_report path (reportPlayer, live) — validates another player's
  // stored `chamber_of_trial` UserData.
  //
  // ATK was the one threshold the two live handlers disagreed on: checkData
  // used 9999, reportPlayer used 3000. Unified on 9999 by user decision
  // (2026-08-28). 3000 was the older value and would flag ordinary endgame
  // players on the report path while the same players passed the save-path
  // check — the drift, not either number, was the actual defect.
  REPORT_ATK_MAX: 9_999,
  REPORT_MAXHP_MAX: 25_000,
  REPORT_DEF_MAX: 3_000,
  REPORT_CDMG_MAX: 1_500,
  REPORT_ENHANCEMENT_MAX: 15,
} as const;

export type AnticheatThresholds = { [K in keyof typeof ANTICHEAT_THRESHOLDS]: number };
export const ANTICHEAT_CONFIG_KEY = 'EC2_ANTICHEAT_THRESHOLDS';

export const ANTICHEAT_LIMIT_LABELS: Record<keyof AnticheatThresholds, string> = {
  ATTRIBUTE_BASE_PT: 'Base attribute points', ATTRIBUTE_TOLERANCE: 'Attribute point tolerance',
  ATTRIBUTE_PT_PER_LEVEL: 'Attribute points per level', SAVE_RUBY_MAX: 'Maximum ruby',
  SAVE_ATK_MAX: 'Maximum ATK in save checks', REPORT_ATK_MAX: 'Maximum combat ATK',
  REPORT_MAXHP_MAX: 'Maximum combat HP', REPORT_DEF_MAX: 'Maximum combat defense',
  REPORT_CDMG_MAX: 'Maximum combat critical damage', REPORT_ENHANCEMENT_MAX: 'Maximum equipment enhancement',
};

export function validateAnticheatThresholds(value: unknown): value is AnticheatThresholds {
  if (!isObject(value)) return false;
  const keys = Object.keys(ANTICHEAT_THRESHOLDS) as Array<keyof AnticheatThresholds>;
  return Object.keys(value).length === keys.length && keys.every(key =>
    typeof value[key] === 'number' && Number.isSafeInteger(value[key]) && (value[key] as number) >= 0
    && (value[key] as number) <= 2147483647
    && (key === 'ATTRIBUTE_TOLERANCE' || (value[key] as number) > 0));
}

// ─── Shared helpers ─────────────────────────────────────────────────────

/** Never throws, never propagates NaN into a comparison — a missing or
 *  malformed field becomes 0, so a structurally odd payload degrades to
 *  "this field looks like 0", never a crash and never a coerced truthy
 *  flag from a bad type. */
function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function isObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function attributePointCeiling(level: unknown, t: AnticheatThresholds): number {
  return (num(level) - 1) * t.ATTRIBUTE_PT_PER_LEVEL + t.ATTRIBUTE_TOLERANCE + t.ATTRIBUTE_BASE_PT;
}

function isIdentifiedHero(hero: Record<string, unknown>): boolean {
  return typeof hero.HERO === "number" && Number.isInteger(hero.HERO) && HERO_NAMES[hero.HERO] !== undefined;
}

function heroLabel(hero: Record<string, unknown>, index: number): string {
  const id = hero.HERO;
  const name = typeof id === "number" && Number.isInteger(id) ? HERO_NAMES[id] : undefined;
  return name ? `${name} (submitted hero entry ${index + 1})` : `Submitted hero entry ${index + 1} (name unavailable)`;
}

// ─── save_check (checkData) ─────────────────────────────────────────────
// Review checks preserve the legacy stat rules. Item history and outcome
// checks were disabled by the owner on 2026-10-02 due to unreliable ledgers.

function validateSaveData(data: unknown, t: AnticheatThresholds, ignoreUnidentifiedHeroes = false): string[] {
  const reasons: string[] = [];
  const raw = isObject(data) ? data : {};
  // Cloud saves contain a User and UserAdventureData, whereas check-data
  // sends TouchData. Keep both callers on the same checks.
  const user = isObject(raw.userData) ? raw.userData : null;
  const adventure = isObject(raw.heroesData) ? raw.heroesData : {};
  const d = user ? { ...user, rdt: user.ruby, hero: adventure.heroes } : raw;

  const ruby = num(d.rdt);
  if (ruby > t.SAVE_RUBY_MAX) {
    reasons.push(`Ruby exceeds limit (${ruby} > ${t.SAVE_RUBY_MAX})`);
  }

  // Item history and outcome bookkeeping are unreliable and are not checked.

  // Hero: attribute points, then ATK. First violating hero stops the loop
  // (source `break`s the outer for-loop on the first hit either way).
  const heroes = Array.isArray(d.hero) ? d.hero : [];
  for (const [index, entry] of heroes.entries()) {
    const h = isObject(entry) ? entry : {};
    // Older clients omit HERO and can submit boosted temporary story heroes.
    // Skip these entries before both PT and ATK checks, but still check ruby.
    if (ignoreUnidentifiedHeroes && !isIdentifiedHero(h)) continue;
    const maxPT = attributePointCeiling(h.LEVEL, t);
    const totalPT = num(h.STR) + num(h.INT) + num(h.DEX) + num(h.AGI) + num(h.VIT);
    if (totalPT > maxPT) {
      reasons.push(`${heroLabel(h, index)}: Total Attribute PT Exceeds Limit (${totalPT} > ${maxPT})`);
      break;
    }
    const atk = num(h.ATK);
    if (atk > t.SAVE_ATK_MAX) {
      reasons.push(`${heroLabel(h, index)}: ATK Exceeds Limit (${atk} > ${t.SAVE_ATK_MAX})`);
      break;
    }
  }

  return reasons;
}

// ─── player_report (reportPlayer) ───────────────────────────────────────
// Mirrors reportPlayer's live checks against `chamber_of_trial` data: an
// array of floor entries, each with a nested `heroData` (LEVEL/STR/INT/DEX/
// AGI/VIT), an `equipment` array (enhancementLevel), and element-level
// attack/maxHP/defense/critDmg. The source breaks its outer loop on the
// FIRST violating floor entry, so at most one entry's reasons are ever
// produced — this port keeps that behavior (scanning further floors once
// one is already bad adds nothing CloudScript ever reported either).

function checkChamberEntry(entry: unknown, t: AnticheatThresholds): string[] {
  const e = isObject(entry) ? entry : {};
  const hero = isObject(e.heroData) ? e.heroData : {};
  const reasons: string[] = [];

  const maxPT = attributePointCeiling(hero.LEVEL, t);
  const totalPT = num(hero.STR) + num(hero.INT) + num(hero.DEX) + num(hero.AGI) + num(hero.VIT);
  if (totalPT > maxPT) {
    // Source breaks the outer loop immediately here, skipping the
    // equipment/ATK/MAXHP/DEF/CDMG checks for this entry entirely.
    return [`Total Allocated PT Exceeds Limit (${totalPT} > ${maxPT})`];
  }

  const equipment = Array.isArray(hero.equipment) ? hero.equipment : [];
  for (const eq of equipment) {
    const level = num(isObject(eq) ? eq.enhancementLevel : undefined);
    if (level > t.REPORT_ENHANCEMENT_MAX) {
      reasons.push(`Equipment enhancement exceeds limit (${level} > ${t.REPORT_ENHANCEMENT_MAX})`);
      break; // source breaks only the equipment loop here, not the outer one
    }
  }

  const atk = num(e.attack);
  if (atk > t.REPORT_ATK_MAX) reasons.push(`ATK exceeds limit (${atk} > ${t.REPORT_ATK_MAX})`);

  const maxHp = num(e.maxHP);
  if (maxHp > t.REPORT_MAXHP_MAX) reasons.push(`MAXHP exceeds limit (${maxHp} > ${t.REPORT_MAXHP_MAX})`);

  const def = num(e.defense);
  if (def > t.REPORT_DEF_MAX) reasons.push(`DEF exceeds limit (${def} > ${t.REPORT_DEF_MAX})`);

  const cdmg = num(e.critDmg);
  if (cdmg > t.REPORT_CDMG_MAX) reasons.push(`CDMG exceeds limit (${cdmg} > ${t.REPORT_CDMG_MAX})`);

  return reasons;
}

function validateReportedPlayerData(data: unknown, t: AnticheatThresholds): string[] {
  const chamberData = Array.isArray(data) ? data : [];
  for (const entry of chamberData) {
    const reasons = checkChamberEntry(entry, t);
    if (reasons.length > 0) return reasons;
  }
  return [];
}

// ─── The one exported entry point ───────────────────────────────────────

export type AnticheatSource = "save_check" | "player_report";

/**
 * `data` is the shape appropriate to `source`:
 *   - "save_check": the caller's own submitted save blob (checkData's
 *     `dataToCheck`, i.e. `JSON.parse(args.phData)`).
 *   - "player_report": the reported player's stored `chamber_of_trial`
 *     UserData (an array of floor entries).
 *
 * Returns reasons — empty means clean. Never throws: every field access
 * goes through defensive extraction (`num`, `isObject`, `Array.isArray`),
 * so missing keys, empty arrays, nulls, and wrong types degrade to "this
 * looks like zero / empty" rather than a crash.
 */
export function validatePlayerData(source: AnticheatSource, data: unknown, t: AnticheatThresholds = ANTICHEAT_THRESHOLDS): string[] {
  const touchData = !(isObject(data) && isObject(data.userData));
  return source === "save_check" ? validateSaveData(data, t, touchData) : validateReportedPlayerData(data, t);
}

/** Cloud snapshots are not TouchData. Read only the actual saved fields;
 * top-level rdt/hero supplied alongside them must not override these values.
 * Saved HeroData has allocated attributes, but no computed combat ATK/HP.
 */
export function validateCloudSave(data: unknown, checkHistory = true, t: AnticheatThresholds = ANTICHEAT_THRESHOLDS): string[] {
  const cloud = isObject(data) ? data : {};
  const user = isObject(cloud.userData) ? cloud.userData : {};
  const heroes = isObject(cloud.heroesData) ? cloud.heroesData.heroes : undefined;
  const reasons = validateSaveData({
    rdt: user.ruby,
    hero: heroes,
    historyAdded: checkHistory ? user.historyAdded : undefined,
    historyRemoved: checkHistory ? user.historyRemoved : undefined,
    valueBeforeNewestUpdate: checkHistory ? user.valueBeforeNewestUpdate : undefined,
    outcome: checkHistory ? user.outcome : undefined,
  }, t);
  if (user.ruby !== undefined && (typeof user.ruby !== "number" || !Number.isFinite(user.ruby) || user.ruby < 0)) {
    reasons.push("Invalid saved ruby value");
  }
  if (heroes !== undefined && (!Array.isArray(heroes) || heroes.some(h => !isObject(h) ||
      !Number.isSafeInteger(h.LEVEL) || num(h.LEVEL) < 1 ||
      ["STR", "INT", "DEX", "AGI", "VIT"].some(key => !Number.isSafeInteger(h[key]) || num(h[key]) < 0)))) {
    reasons.push("Missing or invalid saved hero attributes");
  }
  return reasons;
}

/** Competition requires a complete party snapshot; omitted/wrongly typed
 * fields must not become zero and bypass the existing stat limits. */
export function validateLeaderboardParty(data: unknown, checkLimits = true, t: AnticheatThresholds = ANTICHEAT_THRESHOLDS): string[] {
  if (!Array.isArray(data) || data.length === 0) return ["Missing or invalid leaderboard party"];
  for (const entry of data) {
    if (!isObject(entry) || !isObject(entry.heroData)) return ["Invalid leaderboard hero data"];
    const h = entry.heroData;
    if (!Number.isSafeInteger(h.LEVEL) || num(h.LEVEL) < 1 ||
        ["STR", "INT", "DEX", "AGI", "VIT"].some(key => !Number.isSafeInteger(h[key]) || num(h[key]) < 0) ||
        ["attack", "maxHP", "defense", "critDmg"].some(key => typeof entry[key] !== "number" || !Number.isFinite(entry[key]) || num(entry[key]) < 0) ||
        !Array.isArray(h.equipment) || h.equipment.some(eq => !isObject(eq) ||
          !Number.isSafeInteger(eq.enhancementLevel) || num(eq.enhancementLevel) < 0)) {
      return ["Missing or invalid leaderboard hero stats"];
    }
  }
  return checkLimits ? validateReportedPlayerData(data, t) : [];
}

/** Enforcement uses the same editable limits as review checks.
 * Inspect every hero so a smaller PT warning cannot hide a hard violation.
 * PT uses the configured ceiling, including its existing tolerance.
 */
export function hardBanReasons(kind: 'touch' | 'cloud' | 'party', data: unknown,
  t: AnticheatThresholds = ANTICHEAT_THRESHOLDS): string[] {
  const d = isObject(data) ? data : {};
  const user = isObject(d.userData) ? d.userData : {};
  const savedHeroes = isObject(d.heroesData) ? d.heroesData.heroes : [];
  const ruby = kind === 'cloud' ? user.ruby : kind === 'touch' ? d.rdt : undefined;
  const reasons: string[] = [];
  if (num(ruby) > t.SAVE_RUBY_MAX) reasons.push(`Ruby exceeds limit (${num(ruby)} > ${t.SAVE_RUBY_MAX})`);
  const entries = kind === 'party' ? data : kind === 'cloud' ? savedHeroes : d.hero;
  if (!Array.isArray(entries)) return reasons;
  for (const [index, entry] of entries.entries()) {
    if (!isObject(entry)) continue;
    const hero = kind === 'party' ? entry.heroData : entry;
    if (kind === 'touch' && !isIdentifiedHero(entry)) continue;
    const atk = kind === 'party' ? entry.attack : kind === 'touch' ? entry.ATK : undefined;
    const atkMax = kind === 'touch' ? t.SAVE_ATK_MAX : t.REPORT_ATK_MAX;
    if (num(atk) > atkMax) reasons.push(`Entry ${index + 1}: ATK exceeds limit (${num(atk)} > ${atkMax})`);
    if (!isObject(hero) || !Number.isSafeInteger(hero.LEVEL) || num(hero.LEVEL) < 1 ||
        ['STR', 'INT', 'DEX', 'AGI', 'VIT'].some(key => !Number.isSafeInteger(hero[key]) || num(hero[key]) < 0)) continue;
    const total = num(hero.STR) + num(hero.INT) + num(hero.DEX) + num(hero.AGI) + num(hero.VIT);
    const ceiling = attributePointCeiling(hero.LEVEL, t);
    if (total - ceiling >= 1000) reasons.push(`${heroLabel(hero, index)}: PT exceeds tolerated ceiling by at least 1000 (${total} >= ${ceiling + 1000})`);
  }
  return reasons;
}

/** Resolve retained evidence shapes without interpreting human-written reasons. */
export function hardBanFlagPayload(source: string, payload: unknown, t: AnticheatThresholds): string[] {
  const d = isObject(payload) ? payload : {};
  if (source === 'save_check') return hardBanReasons(isObject(d.userData) ? 'cloud' : 'touch', payload, t);
  if (source === 'player_report') return hardBanReasons('party', payload, t);
  if (source !== 'full_scan' && source !== 'leaderboard_submit') return [];
  const party = isObject(d.submission) ? d.submission : isObject(d.leaderboard) ? d.leaderboard : d;
  return [...hardBanReasons('cloud', d.cloud, t), ...hardBanReasons('party', party.heroData, t)];
}
